import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ConnectionStatusIcon from '../shared/ConnectionStatusIcon';
import { useSiteConfigContext } from '../../contexts/SiteConfigContext';

import config from '../../config.json';
import { Typography } from '@mui/material';

import { useClerk } from '@clerk/clerk-react';
import { useAuth, UserButton } from '@clerk/clerk-react';
import { ClerkAccountSkin, ClerkSignInSkin } from './ClerkSkins';
// ADDED — custom user button for comparison
import JernyUserButton from '../JernyUserComponents/JernyUserButton';
import CalendarPopup from './CalendarPopup';

/**
 * Live clock — click opens CalendarPopup.
 * Uses monospace font to prevent layout shift as digits change width.
 */
function ClockDisplay() {
    const [now, setNow] = useState(new Date());
    const [calOpen, setCalOpen] = useState(false);
    const [calPos, setCalPos] = useState({ top: 0, right: 0 });
    const ref = useRef(null);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const time = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const date = now.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });

    const handleClick = () => {
        if (calOpen) {
            setCalOpen(false);
            return;
        }
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setCalPos({
                top: rect.bottom + 8,
                // CHANGED — -250 verschuift popup 250px naar rechts
                right: window.innerWidth - rect.right - 250,
            });
        }
        setCalOpen(true);
    };

    return (
        <>
            <Box
                ref={ref}
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    lineHeight: 1,
                    minWidth: 72,
                    cursor: 'pointer',
                    borderRadius: '4px',
                    px: '4px',
                    py: '2px',
                    '&:hover': { bgcolor: 'rgba(102,178,255,0.06)' },
                }}
            >
                {/* CHANGED — monospace font prevents layout shift on digit width changes */}
                <Typography sx={{ color: '#a3d1ff', fontSize: 12, fontFamily: "'DM Mono', 'Roboto Mono', monospace", fontWeight: 500 }}>{time}</Typography>
                <Typography sx={{ color: '#a3d1ff', fontSize: 10, fontFamily: "'DM Sans', sans-serif", opacity: 0.6 }}>{date}</Typography>
            </Box>

            {calOpen && <CalendarPopup anchorPos={calPos} onClose={() => setCalOpen(false)} />}
        </>
    );
}

/**
 * AppBar action buttons — sign in/up or user button + connection status + clock.
 */
export default function AppBarActions() {
    const { isSignedIn } = useAuth();
    const { openSignIn } = useClerk();
    const siteConfig = useSiteConfigContext();

    if (!isSignedIn) {
        return (
            <Box sx={{ display: 'flex', gap: 1 }}>
                {siteConfig?.loginActive && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => openSignIn(ClerkSignInSkin)}
                        sx={{
                            color: '#a3d1ff',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(102, 178, 255, 0.08)' },
                        }}
                    >
                        Sign in
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {config.appMode !== 'PROD' && (
                <Typography sx={{ color: '#a3d1ff' }}>{config.appMode}</Typography>
            )}

            <ClockDisplay />

            <ConnectionStatusIcon />

            {/* ADDED — custom user button next to Clerk for comparison */}
            <JernyUserButton />
            {/* <UserButton userProfileUrl="/dashboard" appearance={ClerkAccountSkin} /> */}
        </Box>
    );
}
